import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Ban, Loader2, MapPin, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth, ADMIN_ROLES } from "../../context/AuthContext";
import api, { apiError } from "../../lib/api";
import { StatusBadge } from "../TrackPage";
import ShipmentDocs from "../../components/ShipmentDocs";

const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("if_token")}`;

export default function ShipmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const isAdminPanel = user && ADMIN_ROLES.includes(user.role);
  const canSeePaid = isAdminPanel || ["agent", "franchise"].includes(user.role) || s?.created_by === user.id;

  const load = () => api.get(`/shipments/${id}`).then((r) => setS(r.data)).catch(() => toast.error("Shipment not found"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!s) return <p className="py-20 text-center text-slate-400">Loading…</p>;

  const cancel = async () => {
    if (!window.confirm("Cancel this shipment? Wallet refund applies for prepaid orders.")) return;
    setCancelling(true);
    try {
      await api.post(`/shipments/${s.id}/cancel`);
      toast.success("Shipment cancelled");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCancelling(false);
    }
  };

  const events = [...(s.tracking || [])].sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
  const cancellable = ["Order Created", "Pickup Scheduled", "Pickup Assigned"].includes(s.status);

  return (
    <div data-testid="shipment-detail" className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-navy">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-sm text-slate-500">{s.order_id}</p>
          <h1 className="font-mono font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">{s.awb}</h1>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={s.status} />
          <Link to={`/track/${s.awb}`} data-testid="detail-public-track-btn"
                className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-navy hover:bg-slate-100">
            <Printer className="mr-1 inline h-3.5 w-3.5" /> Public Tracking
          </Link>
          {cancellable && (
            <button onClick={cancel} disabled={cancelling} data-testid="detail-cancel-btn"
                    className="flex items-center gap-1.5 rounded-full bg-red-50 px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-60">
              {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Cancel
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="mb-5 font-heading text-lg font-bold text-navy">Tracking Timeline</h3>
            <ol className="relative ml-3 space-y-6 border-l-2 border-slate-200 pl-6">
              {events.map((ev, i) => (
                <li key={i} className="relative">
                  <span className={`absolute -left-[31px] grid h-5 w-5 place-items-center rounded-full border-2 ${i === events.length - 1 ? "border-saffron bg-saffron" : "border-slate-300 bg-white"}`} />
                  <p className="font-heading text-sm font-bold text-navy">{ev.status}</p>
                  {ev.description && <p className="text-xs text-slate-600">{ev.description}</p>}
                  <p className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                    {ev.location && <><MapPin className="h-3 w-3" />{ev.location}</>}
                    <span className="font-mono">{ev.timestamp ? new Date(ev.timestamp).toLocaleString("en-IN") : ""}</span>
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm">
            <h3 className="mb-4 font-heading text-lg font-bold text-navy">Summary</h3>
            {[["Courier", `${s.courier_name} · ${s.service}`], ["Payment", s.payment_type],
              ["Weight", `${s.chargeable_weight} kg (chargeable)`], ["Base Rate", `₹${s.base_price}`],
              ["You Paid", `₹${s.customer_price}`], ["Expected", s.expected_delivery || "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-100 py-2.5 last:border-0">
                <span className="text-slate-500">{k}</span><span className="font-semibold text-navy">{v}</span>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm">
            <h3 className="mb-4 font-heading text-lg font-bold text-navy">Addresses</h3>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Pickup</p>
            <p className="mt-1 font-semibold text-navy">{s.pickup?.name}</p>
            <p className="text-slate-600">{s.pickup?.address}, {s.pickup?.city} {s.pickup?.pincode}</p>
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-400">Delivery</p>
            <p className="mt-1 font-semibold text-navy">{s.delivery?.name}</p>
            <p className="text-slate-600">{s.delivery?.address}, {s.delivery?.city} {s.delivery?.zipcode || s.delivery?.pincode}{s.delivery?.country ? `, ${s.delivery.country}` : ""}</p>
          </div>
          {s.is_international && (
            <div className="rounded-2xl border border-blue-200 bg-white p-6 text-sm" data-testid="intl-details-card">
              <h3 className="mb-4 font-heading text-lg font-bold text-navy">International Details</h3>
              <div className="space-y-2">
                {[["Service", `${s.service_name || s.courier_name} · ${s.service_type}`],
                  ["Shipping Date", s.shipping_date || "—"],
                  ["Courier Tracking No.", s.tracking_number || "Pending — assigned by admin after handover"],
                  ["Payment Mode", (s.payment_mode || "").toUpperCase()],
                  ["Declared Value", `₹${(s.total_value || 0).toLocaleString("en-IN")}`]].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 py-2 last:border-0">
                    <span className="text-slate-500">{k}</span><span className="font-semibold text-navy">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-3">
                {(s.boxes || []).map((b) => (
                  <div key={b.box_no} className="rounded-xl border border-slate-200 p-3" data-testid={`detail-box-${b.box_no}`}>
                    <p className="text-xs font-bold text-navy">Box {b.box_no}/{(s.boxes || []).length} · {b.weight} kg · billed {b.chargeable_weight} kg · {b.length}×{b.width}×{b.height} cm</p>
                    {(b.items || []).length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                        {b.items.map((it, i) => <li key={i}>• {it.name} ×{it.quantity} — ₹{it.value}</li>)}
                      </ul>
                    )}
                    {b.photo && <a href={fileUrl(b.photo)} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs font-bold text-express hover:underline" data-testid={`detail-box-${b.box_no}-photo`}>Box photo</a>}
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs">
                <div className="flex justify-between"><span>Received from customer</span><b>₹{(s.received_from_customer || 0).toLocaleString("en-IN")}</b></div>
                {canSeePaid && (
                  <div className="mt-1 flex justify-between"><span>Paid to INDIAFAST</span><b>₹{(s.paid_to_indiafast || 0).toLocaleString("en-IN")}</b></div>
                )}
                {isAdminPanel && s.profit != null && (
                  <div className="mt-1 flex justify-between border-t border-slate-200 pt-1">
                    <span>Profit</span><b data-testid="detail-profit" className={(s.profit || 0) >= 0 ? "text-emerald-600" : "text-red-500"}>₹{(s.profit || 0).toLocaleString("en-IN")}</b>
                  </div>
                )}
              </div>
              {(s.payment_proofs || []).length > 0 && (
                <div className="mt-3 flex gap-2">
                  {s.payment_proofs.map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-express hover:border-express" data-testid={`detail-proof-${i}`}>Payment proof {i + 1}</a>
                  ))}
                </div>
              )}
            </div>
          )}
          <ShipmentDocs shipment={s} />
        </div>
      </div>
    </div>
  );
}
